(function () {
  const roots = Array.from(document.querySelectorAll("[data-resource-calculator]"));
  if (!roots.length) return;

  const standardAmpSizes = [0.5, 1, 2, 3, 5, 6, 8, 10, 12, 15, 20, 25, 30, 35, 40, 45, 50, 60, 70, 80, 90, 100, 110, 125, 150, 175, 200, 225, 250, 300, 350, 400, 450, 500, 600];
  const standardVaSizes = [25, 50, 75, 100, 150, 200, 250, 300, 500, 750, 1000, 1500, 2000, 3000, 5000];
  const standardHeaterWatts = [25, 50, 75, 100, 150, 200, 250, 300, 400, 500, 800, 1000];
  const standardSwitchPorts = [5, 8, 10, 12, 16, 24, 48];
  const standardCtratios = [25, 50, 75, 100, 150, 200, 300, 400, 500, 600, 800, 1000, 1200, 1500, 1600, 2000, 2500, 3000, 4000];

  const selectOptions = (pairs) => pairs.map(([value, label]) => ({ value, label }));
  const toNumber = (value) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const toInteger = (value) => Math.max(0, Math.round(toNumber(value)));
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const nextStandard = (value, standards) => {
    for (const standard of standards) {
      if (value <= standard) return standard;
    }
    return standards[standards.length - 1];
  };
  const formatNumber = (value, digits = 2) => {
    if (!Number.isFinite(value)) return "0";
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: value % 1 === 0 ? 0 : Math.min(digits, 1),
      maximumFractionDigits: digits,
    }).format(value);
  };
  const escapeHtml = (value) =>
    String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const makePrompt = (headline, summary) => ({ headline, summary, bullets: [], tone: "neutral" });

  const makeHtml = (result) => {
    const bullets = result.bullets && result.bullets.length
      ? `<ul class="resource-tool__list">${result.bullets.map((bullet) => `<li>${escapeHtml(bullet)}</li>`).join("")}</ul>`
      : "";
    const toneClass = result.tone ? ` resource-tool__result--${result.tone}` : "";
    return `<div class="resource-tool__summary${toneClass}"><strong>${escapeHtml(result.headline || "Starting point")}</strong><p>${escapeHtml(result.summary || "")}</p>${bullets}</div>`;
  };

  const renderFields = (root, definition) => {
    const container = root.querySelector("[data-calculator-fields]");
    if (!container) return;
    container.innerHTML = definition.fields.map((field) => {
      if (field.type === "select") {
        const options = field.options
          .map((option) => `<option value="${escapeHtml(option.value)}"${option.value === field.value ? " selected" : ""}>${escapeHtml(option.label)}</option>`)
          .join("");
        return `<label><span>${escapeHtml(field.label)}</span><select data-calculator-field="${escapeHtml(field.key)}">${options}</select></label>`;
      }
      return `<label><span>${escapeHtml(field.label)}</span><input data-calculator-field="${escapeHtml(field.key)}" type="number" value="${escapeHtml(field.value || "")}" step="${escapeHtml(field.step || "any")}" min="${escapeHtml(field.min || "0")}" inputmode="decimal"></label>`;
    }).join("");
  };

  const getValues = (root) => {
    const values = {};
    root.querySelectorAll("[data-calculator-field]").forEach((field) => {
      values[field.dataset.calculatorField] = field.value;
    });
    return values;
  };

  const choiceDirection = (key, title) => {
    if (key.includes("sensor-type")) return "sensorType";
    if (key.includes("sensor-range")) return "sensorRange";
    if (key.includes("photoelectric")) return "photoelectric";
    if (key.includes("ultrasonic")) return "ultrasonic";
    if (key.includes("hmi-screen")) return "hmi";
    if (key.includes("managed-switch")) return "switchPorts";
    if (key.includes("remote-i-o")) return "remoteIo";
    if (key.includes("analog-card-count")) return "analogCardCount";
    if (key.includes("analog-signal-resolution")) return "analogResolution";
    if (key.includes("analog-signal-scaling")) return "analogScaling";
    if (key.includes("encoder-resolution")) return "encoderResolution";
    if (key.includes("pilot-device")) return "pilotDevices";
    if (key.includes("current-transformer-ratio")) return "ctRatio";
    if (key.includes("panel-cooling")) return "panelCooling";
    if (key.includes("enclosure-heater")) return "heater";
    if (key.includes("enclosure-rating")) return "enclosure";
    if (key.includes("fuse-class")) return "fuseClass";
    if (key.includes("fuse-sizing")) return "fuseSizing";
    if (key.includes("branch-protection")) return "branchProtection";
    if (key.includes("transformer-inrush")) return "transformerInrush";
    if (key.includes("transformer")) return "transformerSizing";
    if (key.includes("redundant-power-supply")) return "redundantPower";
    if (key.includes("reserve-capacity")) return "reserveCapacity";
    if (key.includes("power-budget") || key.includes("24vdc-load-budget") || key.includes("control-circuit-current-budget")) return "powerBudget";
    if (key.includes("soft-starter")) return "softStarter";
    if (key.includes("manual-motor-starter")) return "manualStarter";
    if (key.includes("combination-starter-data-capture")) return "starterCapture";
    if (key.includes("motor-starter-selection")) return "motorStarter";
    return title.toLowerCase().includes("calculator") ? "powerBudget" : "fallback";
  };

  const definitions = {
    transformerSizing() {
      return {
        fields: [
          { key: "steady_va", label: "Steady-state burden (VA)", value: "80", step: "1" },
          { key: "largest_inrush_va", label: "Largest inrush burden (VA)", value: "120", step: "1" },
          { key: "simultaneous_inrush", label: "Simultaneous inrush loads", value: "1", step: "1" },
          { key: "margin_pct", label: "Design margin (%)", value: "25", step: "1" },
        ],
        compute(values) {
          const steady = toNumber(values.steady_va);
          if (steady <= 0) return makePrompt("Enter the steady-state burden", "Start with the actual control-circuit burden before using the helper.");
          const inrush = toNumber(values.largest_inrush_va);
          const simultaneous = Math.max(1, toInteger(values.simultaneous_inrush));
          const raw = Math.max(steady * 1.25, steady + inrush * simultaneous) * (1 + clamp(toNumber(values.margin_pct), 0, 100) / 100);
          const suggested = nextStandard(raw, standardVaSizes);
          return {
            headline: `${formatNumber(suggested, 0)} VA starting size`,
            summary: "This is a first-pass control-transformer size only. Final sizing still needs real coil inrush data and manufacturer tables.",
            bullets: [`Calculated burden before standard size: ${formatNumber(raw, 0)} VA`, "Check simultaneous inrush timing before release."],
            tone: "good",
          };
        },
      };
    },
    transformerInrush() {
      return {
        fields: [
          { key: "transformer_va", label: "Transformer size (VA)", value: "250", step: "1" },
          { key: "steady_va", label: "Steady-state burden (VA)", value: "90", step: "1" },
          { key: "inrush_va", label: "Simultaneous inrush burden (VA)", value: "120", step: "1" },
        ],
        compute(values) {
          const transformer = toNumber(values.transformer_va);
          const steady = toNumber(values.steady_va);
          const inrush = toNumber(values.inrush_va);
          if (transformer <= 0) return makePrompt("Enter the transformer size", "The inrush check starts with the actual transformer size.");
          const available = transformer - steady;
          const pass = available >= inrush;
          return {
            headline: pass ? "First-pass inrush check passes" : "First-pass inrush check is short",
            summary: pass ? "The remaining VA appears to cover the listed inrush event." : "The listed inrush event is larger than the remaining VA after steady burden.",
            bullets: [`Available VA after steady load: ${formatNumber(available, 0)} VA`, "Published coil inrush curves still decide the real answer."],
            tone: pass ? "good" : "warn",
          };
        },
      };
    },
    fuseSizing() {
      return {
        fields: [
          { key: "load_current", label: "Load current (A)", value: "8", step: "0.1" },
          { key: "continuous", label: "Load duty", type: "select", value: "continuous", options: selectOptions([["continuous", "Continuous"], ["noncontinuous", "Non-continuous"]]) },
          { key: "inrush", label: "Inrush profile", type: "select", value: "moderate", options: selectOptions([["low", "Low"], ["moderate", "Moderate"], ["high", "High"]]) },
          { key: "objective", label: "Protection objective", type: "select", value: "general", options: selectOptions([["general", "General control circuit"], ["motor", "Motor or high inrush"], ["fast", "Fast-acting electronics"]]) },
        ],
        compute(values) {
          const load = toNumber(values.load_current);
          if (load <= 0) return makePrompt("Enter the circuit current", "Fuse sizing starts with the real circuit current.");
          const raw = load * (values.continuous === "continuous" ? 1.25 : 1.1) * (values.inrush === "high" ? 1.4 : values.inrush === "moderate" ? 1.2 : 1.05);
          const suggested = nextStandard(raw, standardAmpSizes);
          const family = values.objective === "fast" ? "fast-acting class family" : values.objective === "motor" ? (suggested > 30 ? "Class J" : "Class CC") : "Class CC or Class J";
          return {
            headline: `${formatNumber(suggested)} A first-pass size`,
            summary: `Use ${family} as a starting direction only, then verify the actual device family and SCCR path.`,
            bullets: [`Raw pre-standard size: ${formatNumber(raw)} A`, "Time-current review is still required before release."],
            tone: "good",
          };
        },
      };
    },
    fuseClass() {
      return {
        fields: [
          { key: "current_a", label: "Control-circuit current (A)", value: "12", step: "0.1" },
          { key: "space", label: "Available space", type: "select", value: "compact", options: selectOptions([["compact", "Compact"], ["standard", "Standard"]]) },
          { key: "objective", label: "Primary objective", type: "select", value: "high-sccr", options: selectOptions([["general", "General branch circuit"], ["high-sccr", "High SCCR in compact space"], ["service", "Service-friendly replacement"]]) },
        ],
        compute(values) {
          const current = toNumber(values.current_a);
          if (current <= 0) return makePrompt("Enter the circuit current", "Start with the real circuit current and protection job.");
          let headline = "Class CC shortlist";
          if (current > 30 || values.space === "standard") headline = "Class J shortlist";
          if (values.objective === "service" && current <= 30) headline = "Class CC or midget shortlist";
          return {
            headline,
            summary: "This is a class direction only. Holder fit, amp range, and documentation still have to be checked.",
            bullets: [`Entered circuit current: ${formatNumber(current)} A`, "Confirm holder family and published SCCR data before ordering."],
            tone: "good",
          };
        },
      };
    },
    branchProtection() {
      return {
        fields: [
          { key: "starter_current", label: "Starter or motor current (A)", value: "28", step: "0.1" },
          { key: "fault", label: "Fault-current concern", type: "select", value: "high", options: selectOptions([["standard", "Standard"], ["high", "High"], ["unknown", "Unknown"]]) },
          { key: "preference", label: "Maintenance preference", type: "select", value: "fuse", options: selectOptions([["fuse", "Fuse-based"], ["breaker", "Breaker-based"], ["either", "Either"]]) },
        ],
        compute(values) {
          const current = toNumber(values.starter_current);
          if (current <= 0) return makePrompt("Enter the starter current", "Branch-protection shortlisting begins with the real current and protection role.");
          let headline = "Fuse-based starter branch-protection path";
          if (values.preference === "breaker" && values.fault !== "high") headline = "Breaker-based starter branch-protection path";
          if (values.preference === "either" && values.fault !== "high") headline = "Fuse or breaker path worth comparing";
          return {
            headline,
            summary: "Use this as a first-pass direction only. UL 508A context, SCCR path, and disconnect role still decide the final answer.",
            bullets: [`Entered current: ${formatNumber(current)} A`, "Starter family coordination data still needs review."],
            tone: "good",
          };
        },
      };
    },
    enclosure() {
      return {
        fields: [
          { key: "location", label: "Environment", type: "select", value: "indoor", options: selectOptions([["indoor", "Indoor dry"], ["dust", "Indoor dusty"], ["outdoor", "Outdoor weather"], ["washdown", "Washdown"], ["corrosive", "Corrosive"]]) },
          { key: "heat", label: "Internal heat load", type: "select", value: "low", options: selectOptions([["low", "Low"], ["medium", "Medium"], ["high", "High"]]) },
        ],
        compute(values) {
          let rating = "NEMA 12 / IP54";
          if (values.location === "outdoor") rating = "NEMA 3R or NEMA 4";
          if (values.location === "washdown") rating = "NEMA 4X";
          if (values.location === "corrosive") rating = "NEMA 4X with corrosion-resistant materials";
          return {
            headline: rating,
            summary: "This is a first-pass enclosure direction only. Cable entry, materials, and real exposure still need review.",
            bullets: [values.heat === "high" ? "Higher internal heat means thermal accessories should be part of the shortlist." : "Keep internal heat and service access in the final enclosure review."],
            tone: "good",
          };
        },
      };
    },
    heater() {
      return {
        fields: [
          { key: "volume_cuft", label: "Enclosure volume (cu ft)", value: "12", step: "0.1" },
          { key: "temp_rise_f", label: "Target temperature rise (F)", value: "15", step: "1" },
        ],
        compute(values) {
          const volume = toNumber(values.volume_cuft);
          const rise = toNumber(values.temp_rise_f);
          if (volume <= 0 || rise <= 0) return makePrompt("Enter the enclosure size and target rise", "Heater sizing starts with enclosure volume and temperature lift.");
          const raw = volume * rise * 0.6;
          const suggested = nextStandard(raw, standardHeaterWatts);
          return {
            headline: `${formatNumber(suggested, 0)} W heater starting size`,
            summary: "This is a first-pass heater size only. Cabinet leakage and thermostat strategy still matter.",
            bullets: [`Calculated heater load: ${formatNumber(raw, 0)} W`, "Drain and condensation control may matter as much as heater wattage."],
            tone: "good",
          };
        },
      };
    },
    panelCooling() {
      return {
        fields: [
          { key: "heat_load_w", label: "Internal heat load (W)", value: "250", step: "1" },
          { key: "delta_f", label: "Allowable temperature rise (F)", value: "15", step: "1", min: "1" },
        ],
        compute(values) {
          const heat = toNumber(values.heat_load_w);
          const deltaF = Math.max(1, toNumber(values.delta_f));
          if (heat <= 0) return makePrompt("Enter the heat load", "Airflow review starts with the real panel heat load.");
          const airflow = (heat * 1.76) / deltaF;
          return {
            headline: `${formatNumber(nextStandard(airflow, [20, 50, 100, 150, 200, 300, 500, 700, 1000]), 0)} CFM starting airflow`,
            summary: "This is a simple forced-air starting point only. Actual air path losses and filter loading still need review.",
            bullets: [`Calculated airflow need: ${formatNumber(airflow, 0)} CFM`, "Use sealed cooling methods if the enclosure cannot be opened to ambient air."],
            tone: "good",
          };
        },
      };
    },
    powerBudget(title) {
      return {
        fields: [
          { key: "steady_current", label: "Steady-state current (A)", value: "3.2", step: "0.1" },
          { key: "peak_current", label: "Peak or startup current (A)", value: "4.5", step: "0.1" },
          { key: "reserve_pct", label: "Reserve margin (%)", value: "25", step: "1" },
        ],
        compute(values) {
          const steady = toNumber(values.steady_current);
          const peak = Math.max(steady, toNumber(values.peak_current));
          if (peak <= 0) return makePrompt("Enter the load current", `Start with the real load before using ${title.toLowerCase()}.`);
          const recommended = nextStandard(peak * (1 + clamp(toNumber(values.reserve_pct), 0, 100) / 100), standardAmpSizes);
          return {
            headline: `${formatNumber(recommended)} A starting budget`,
            summary: "This covers the higher of the steady and peak load, plus the reserve margin entered.",
            bullets: [`Peak load considered: ${formatNumber(peak)} A`, "Check inrush clusters, redundancy, and temperature derating before final selection."],
            tone: "good",
          };
        },
      };
    },
    reserveCapacity() {
      return {
        fields: [
          { key: "supply_a", label: "Supply rating (A)", value: "10", step: "0.1" },
          { key: "load_a", label: "Current load (A)", value: "6.5", step: "0.1" },
        ],
        compute(values) {
          const supply = toNumber(values.supply_a);
          const load = toNumber(values.load_a);
          if (supply <= 0) return makePrompt("Enter the supply rating", "Reserve capacity starts with the actual supply size.");
          const reserve = supply - load;
          return {
            headline: `${formatNumber(reserve)} A reserve capacity`,
            summary: reserve >= 0 ? "Positive reserve remains on the entered supply." : "The entered load is already over the supply rating.",
            bullets: ["Check derating, inrush, and future expansion before trusting the reserve number."],
            tone: reserve >= 0 ? "good" : "warn",
          };
        },
      };
    },
    redundantPower() {
      return {
        fields: [
          { key: "total_load_a", label: "Total 24 VDC load (A)", value: "8", step: "0.1" },
          { key: "single_supply_a", label: "One supply rating (A)", value: "10", step: "0.1" },
          { key: "target_pct", label: "Target max loading on one supply (%)", value: "80", step: "1" },
        ],
        compute(values) {
          const load = toNumber(values.total_load_a);
          const supply = toNumber(values.single_supply_a);
          if (load <= 0 || supply <= 0) return makePrompt("Enter the load and supply size", "Redundant supply review starts with the real load and one-supply rating.");
          const nPlusOneOk = load <= supply;
          return {
            headline: nPlusOneOk ? "Single-supply survivability looks possible" : "One supply cannot carry the full load",
            summary: nPlusOneOk ? "One supply could carry the load on paper." : "The pair is not true N+1 at the entered rating.",
            bullets: [`Preferred split at normal operation: ${formatNumber(load / 2)} A per supply`, "Check redundancy modules and derating before release."],
            tone: nPlusOneOk ? "good" : "warn",
          };
        },
      };
    },
    ctRatio() {
      return {
        fields: [
          { key: "primary_current", label: "Expected primary current (A)", value: "140", step: "1" },
          { key: "secondary", label: "Desired secondary", type: "select", value: "5", options: selectOptions([["5", "5 A"], ["1", "1 A"]]) },
          { key: "margin_pct", label: "Headroom above load (%)", value: "20", step: "1" },
        ],
        compute(values) {
          const primary = toNumber(values.primary_current);
          const secondary = toNumber(values.secondary) || 5;
          if (primary <= 0) return makePrompt("Enter the primary current", "CT ratio review starts with the real current range.");
          const ratioPrimary = nextStandard((primary * (1 + clamp(toNumber(values.margin_pct), 0, 100) / 100)) / secondary, standardCtratios);
          return {
            headline: `${formatNumber(ratioPrimary, 0)}:${formatNumber(secondary, 0)} CT starting ratio`,
            summary: "This is a first-pass ratio only. Accuracy class and burden still need review.",
            bullets: [`Expected secondary at load: ${formatNumber(primary / ratioPrimary * secondary, 2)} A`, "Protection and metering CTs should not be mixed casually."],
            tone: "good",
          };
        },
      };
    },
  };

  const extendedDefinitions = {
    motorStarter() {
      return {
        fields: [
          { key: "motor_fla", label: "Motor full-load current (A)", value: "18", step: "0.1" },
          { key: "start_severity", label: "Starting severity", type: "select", value: "normal", options: selectOptions([["light", "Light"], ["normal", "Normal"], ["heavy", "Heavy"]]) },
          { key: "speed_control", label: "Variable speed required", type: "select", value: "no", options: selectOptions([["no", "No"], ["yes", "Yes"]]) },
        ],
        compute(values) {
          const fla = toNumber(values.motor_fla);
          if (fla <= 0) return makePrompt("Enter the motor current", "Starter selection should begin with real nameplate current.");
          let path = "Contactor and overload starter";
          if (values.speed_control === "yes") path = "Variable frequency drive";
          else if (values.start_severity === "heavy") path = "Soft starter or heavier-duty starter review";
          return { headline: path, summary: "This is the first control path to compare, not the final part selection.", bullets: [`Motor current entered: ${formatNumber(fla)} A`, "Protection path and enclosure heat still need review."], tone: "good" };
        },
      };
    },
    softStarter() {
      return {
        fields: [
          { key: "motor_fla", label: "Motor FLA (A)", value: "72", step: "0.1" },
          { key: "duty", label: "Starting duty", type: "select", value: "normal", options: selectOptions([["light", "Light"], ["normal", "Normal"], ["heavy", "Heavy"]]) },
        ],
        compute(values) {
          const fla = toNumber(values.motor_fla);
          if (fla <= 0) return makePrompt("Enter the motor current", "Soft-starter review starts with real motor current.");
          const factor = values.duty === "heavy" ? 1.3 : values.duty === "normal" ? 1.2 : 1.15;
          return { headline: `${formatNumber(nextStandard(fla * factor, standardAmpSizes))} A soft-starter range`, summary: "This is a current-range shortlist only.", bullets: ["Bypass strategy and starts-per-hour still matter."], tone: "good" };
        },
      };
    },
    manualStarter() {
      return {
        fields: [
          { key: "motor_fla", label: "Motor FLA (A)", value: "9.2", step: "0.1" },
          { key: "service_factor", label: "Service factor", value: "1.15", step: "0.01" },
        ],
        compute(values) {
          const fla = toNumber(values.motor_fla);
          if (fla <= 0) return makePrompt("Enter the motor current", "Manual motor-starter review starts with the actual FLA.");
          return { headline: `Choose an adjustment range that brackets ${formatNumber(fla)} A`, summary: "This is only the first filter for a manual motor-starter family.", bullets: ["Short-circuit protection and accessory needs still need review."], tone: "good" };
        },
      };
    },
    starterCapture() {
      return {
        fields: [
          { key: "nameplate", label: "Motor nameplate captured", type: "select", value: "yes", options: selectOptions([["yes", "Yes"], ["no", "No"]]) },
          { key: "voltage", label: "Voltage and phase known", type: "select", value: "yes", options: selectOptions([["yes", "Yes"], ["no", "No"]]) },
          { key: "control", label: "Control voltage known", type: "select", value: "no", options: selectOptions([["yes", "Yes"], ["no", "No"]]) },
          { key: "mounting", label: "Mounting details known", type: "select", value: "no", options: selectOptions([["yes", "Yes"], ["no", "No"]]) },
        ],
        compute(values) {
          const total = Object.keys(values).length;
          const complete = Object.values(values).filter((value) => value === "yes").length;
          return { headline: `${Math.round((complete / total) * 100)}% of the intake set is captured`, summary: complete === total ? "The core starter intake is in place." : "The missing items are the ones most likely to stall a replacement or quote path.", bullets: complete === total ? ["Move into starter family and protection review next."] : ["Finish the missing fields before treating the starter path as complete."], tone: complete === total ? "good" : "warn" };
        },
      };
    },
    analogResolution() {
      return {
        fields: [{ key: "eng_span", label: "Engineering span", value: "100", step: "0.1" }, { key: "bits", label: "Bit resolution", value: "12", step: "1" }],
        compute(values) {
          const span = toNumber(values.eng_span);
          const bits = Math.max(1, toInteger(values.bits));
          if (span <= 0) return makePrompt("Enter the engineering span", "Resolution needs the actual engineering span.");
          const increment = span / (Math.pow(2, bits) - 1);
          return { headline: `${formatNumber(increment, 4)} units per count`, summary: "That is the math resolution for the entered span and bit depth.", bullets: ["Noise and sensor accuracy still matter as much as raw count size."], tone: "good" };
        },
      };
    },
    analogScaling() {
      return {
        fields: [{ key: "raw_min", label: "Raw minimum", value: "0", step: "0.1" }, { key: "raw_max", label: "Raw maximum", value: "32767", step: "0.1" }, { key: "eng_min", label: "Engineering minimum", value: "0", step: "0.1" }, { key: "eng_max", label: "Engineering maximum", value: "100", step: "0.1" }, { key: "raw_value", label: "Raw value", value: "16384", step: "0.1" }],
        compute(values) {
          const rawMin = toNumber(values.raw_min);
          const rawMax = toNumber(values.raw_max);
          if (rawMax === rawMin) return makePrompt("Change the raw span", "The raw minimum and maximum cannot match.");
          const scaled = toNumber(values.eng_min) + ((toNumber(values.raw_value) - rawMin) / (rawMax - rawMin)) * (toNumber(values.eng_max) - toNumber(values.eng_min));
          return { headline: `${formatNumber(scaled, 3)} engineering units`, summary: "That is the linearly scaled value for the entered raw point.", bullets: ["Check live zero, card format, and sensor trim before using the number in production."], tone: "good" };
        },
      };
    },
    analogCardCount() {
      return {
        fields: [{ key: "ai_points", label: "Analog inputs needed", value: "18", step: "1" }, { key: "ao_points", label: "Analog outputs needed", value: "6", step: "1" }, { key: "ai_card", label: "AI channels per card", type: "select", value: "8", options: selectOptions([["2", "2"], ["4", "4"], ["8", "8"], ["16", "16"]]) }, { key: "ao_card", label: "AO channels per card", type: "select", value: "4", options: selectOptions([["2", "2"], ["4", "4"], ["8", "8"], ["16", "16"]]) }],
        compute(values) {
          const aiCards = Math.ceil(toInteger(values.ai_points) / Math.max(1, toInteger(values.ai_card)));
          const aoCards = Math.ceil(toInteger(values.ao_points) / Math.max(1, toInteger(values.ao_card)));
          return { headline: `${aiCards + aoCards} analog cards starting point`, summary: `That covers ${aiCards} input card(s) and ${aoCards} output card(s).`, bullets: ["Special signal types and spare channels still need review."], tone: "good" };
        },
      };
    },
    remoteIo() {
      return {
        fields: [{ key: "devices", label: "Remote devices or stations", value: "14", step: "1" }, { key: "per_drop", label: "Preferred devices per drop", value: "4", step: "1" }, { key: "spare_pct", label: "Spare capacity (%)", value: "20", step: "1" }],
        compute(values) {
          const devices = toInteger(values.devices);
          const perDrop = Math.max(1, toInteger(values.per_drop));
          if (devices <= 0) return makePrompt("Enter the device count", "Remote I/O layout starts with the real device count.");
          const drops = Math.ceil(devices / perDrop);
          const planned = Math.ceil(drops * (1 + clamp(toNumber(values.spare_pct), 0, 100) / 100));
          return { headline: `${drops} drop(s) minimum, ${planned} planned`, summary: "The second number includes the spare capacity entered.", bullets: ["Network limits and I/O family constraints still decide the final layout."], tone: "good" };
        },
      };
    },
    switchPorts() {
      return {
        fields: [{ key: "endpoints", label: "Endpoints", value: "9", step: "1" }, { key: "uplinks", label: "Uplinks", value: "2", step: "1" }, { key: "spare_pct", label: "Spare ports (%)", value: "25", step: "1" }],
        compute(values) {
          const raw = (toInteger(values.endpoints) + toInteger(values.uplinks)) * (1 + clamp(toNumber(values.spare_pct), 0, 100) / 100);
          if (raw <= 0) return makePrompt("Enter the port count", "Switch sizing starts with the real endpoint count.");
          return { headline: `${formatNumber(nextStandard(raw, standardSwitchPorts), 0)}-port switch direction`, summary: "This is a port-count starting point only.", bullets: ["PoE, fiber, and ring topology can change the final hardware family."], tone: "good" };
        },
      };
    },
    hmi() {
      return {
        fields: [{ key: "distance_ft", label: "Viewing distance (ft)", value: "3", step: "0.5" }, { key: "density", label: "Information density", type: "select", value: "medium", options: selectOptions([["simple", "Simple"], ["medium", "Medium"], ["dense", "Dense"]]) }],
        compute(values) {
          const distance = toNumber(values.distance_ft);
          let size = "4 to 7 in.";
          if (values.density === "medium" || distance > 3) size = "7 to 10 in.";
          if (values.density === "dense" || distance > 6) size = "10 to 15 in.";
          return { headline: `${size} screen shortlist`, summary: "That range fits the viewing distance and information density entered.", bullets: ["Mounting cutout and controller integration still need review."], tone: "good" };
        },
      };
    },
    sensorType() {
      return {
        fields: [{ key: "target", label: "Target type", type: "select", value: "metal", options: selectOptions([["metal", "Metal"], ["clear", "Clear or reflective"], ["mixed", "Mixed materials"], ["level", "Level or irregular"]]) }, { key: "distance_mm", label: "Detection distance (mm)", value: "50", step: "1" }],
        compute(values) {
          const distance = toNumber(values.distance_mm);
          let choice = "Inductive proximity sensor";
          if (values.target === "clear") choice = "Photoelectric sensor";
          else if (values.target === "level") choice = "Ultrasonic sensor";
          else if (values.target === "mixed" || distance > 300) choice = "Photoelectric or ultrasonic sensor";
          return { headline: choice, summary: "This is the sensing family to compare first from the conditions entered.", bullets: ["Confirm output type, mounting, and contamination risk before release."], tone: "good" };
        },
      };
    },
    sensorRange() {
      return {
        fields: [{ key: "distance_mm", label: "Required distance (mm)", value: "150", step: "1" }, { key: "environment", label: "Environment", type: "select", value: "clean", options: selectOptions([["clean", "Clean"], ["dusty", "Dusty"], ["washdown", "Washdown"]]) }],
        compute(values) {
          const distance = toNumber(values.distance_mm);
          if (distance <= 0) return makePrompt("Enter the target distance", "Sensor-range review starts with the actual target distance.");
          let path = "Short-range inductive or compact photoelectric path";
          if (distance > 300) path = "Medium-range photoelectric or ultrasonic path";
          if (distance > 1000) path = "Longer-range photoelectric path";
          return { headline: path, summary: "The requested distance points toward this sensing range family as a first pass.", bullets: [values.environment === "clean" ? "Confirm the real target and background before release." : "Contamination usually deserves extra range margin."], tone: "good" };
        },
      };
    },
    photoelectric() {
      return {
        fields: [{ key: "clear_target", label: "Target is clear or reflective", type: "select", value: "no", options: selectOptions([["no", "No"], ["yes", "Yes"]]) }, { key: "reflector_ok", label: "Reflector can be used", type: "select", value: "yes", options: selectOptions([["yes", "Yes"], ["no", "No"]]) }, { key: "background_close", label: "Background is close", type: "select", value: "no", options: selectOptions([["no", "No"], ["yes", "Yes"]]) }],
        compute(values) {
          let choice = "Diffuse or background-suppression mode";
          if (values.clear_target === "yes") choice = values.reflector_ok === "yes" ? "Polarized retroreflective mode" : "Through-beam mode";
          else if (values.background_close === "yes") choice = "Background-suppression mode";
          else if (values.reflector_ok === "yes") choice = "Retroreflective mode";
          return { headline: choice, summary: "This is the photoelectric mode to compare first from the entered target and mounting conditions.", bullets: ["Confirm target color, angle, and contamination on the real machine."], tone: "good" };
        },
      };
    },
    ultrasonic() {
      return {
        fields: [{ key: "sensor_max", label: "Sensor max range (mm)", value: "1000", step: "1" }, { key: "target_distance", label: "Target distance (mm)", value: "650", step: "1" }, { key: "required_margin", label: "Desired safety margin (%)", value: "20", step: "1" }],
        compute(values) {
          const max = toNumber(values.sensor_max);
          const target = toNumber(values.target_distance);
          if (max <= 0 || target <= 0) return makePrompt("Enter the sensor and target distances", "Margin review starts with the real sensor range and target distance.");
          const marginPct = ((max - target) / max) * 100;
          return { headline: `${formatNumber(marginPct, 0)}% ultrasonic margin`, summary: marginPct >= toNumber(values.required_margin) ? "The entered sensor range leaves the requested margin." : "The entered sensor range leaves less margin than requested.", bullets: ["Target angle and surface still affect real ultrasonic performance."], tone: marginPct >= toNumber(values.required_margin) ? "good" : "warn" };
        },
      };
    },
    encoderResolution() {
      return {
        fields: [{ key: "travel_per_rev", label: "Travel per revolution (mm)", value: "100", step: "0.1" }, { key: "desired_resolution", label: "Desired resolution (mm)", value: "0.5", step: "0.01" }, { key: "quadrature", label: "Counting mode", type: "select", value: "4", options: selectOptions([["1", "Single edge"], ["2", "Dual edge"], ["4", "Quadrature x4"]]) }],
        compute(values) {
          const travel = toNumber(values.travel_per_rev);
          const resolution = toNumber(values.desired_resolution);
          if (travel <= 0 || resolution <= 0) return makePrompt("Enter travel and resolution", "Encoder resolution starts with the real motion per revolution and the required resolution.");
          const quad = Math.max(1, toInteger(values.quadrature));
          const ppr = Math.ceil((travel / resolution) / quad);
          return { headline: `${formatNumber(ppr, 0)} PPR starting point`, summary: "That is the pulses-per-revolution direction implied by the entered motion and resolution.", bullets: ["Backlash, shaft speed, and controller input limits still need review."], tone: "good" };
        },
      };
    },
    pilotDevices() {
      return {
        fields: [{ key: "pushbuttons", label: "Push buttons", value: "3", step: "1" }, { key: "selector_switches", label: "Selector switches", value: "1", step: "1" }, { key: "pilot_lights", label: "Pilot lights", value: "2", step: "1" }, { key: "e_stops", label: "Emergency stops", value: "1", step: "1" }],
        compute(values) {
          const total = toInteger(values.pushbuttons) + toInteger(values.selector_switches) + toInteger(values.pilot_lights) + toInteger(values.e_stops);
          return { headline: `${total} operator positions to plan`, summary: "This is the first-pass count of pilot-device positions from the items entered.", bullets: ["Contact blocks, legend plates, and mounting depth still need review."], tone: "good" };
        },
      };
    },
    fallback(title) {
      return {
        fields: [{ key: "known_value", label: "Primary known value", value: "0", step: "0.1" }, { key: "margin_pct", label: "Planning margin (%)", value: "20", step: "1" }],
        compute(values) {
          const known = toNumber(values.known_value);
          if (known <= 0) return makePrompt("Enter the first known value", `Use the known job data to get a starting point for ${title}.`);
          return { headline: `${formatNumber(known * (1 + clamp(toNumber(values.margin_pct), 0, 100) / 100))} first-pass planning value`, summary: "Use the guide below to confirm the real family, fit, and manufacturer limits.", bullets: [], tone: "warn" };
        },
      };
    },
  };

  const getDefinition = (key, title) => {
    const family = choiceDirection(key, title);
    const factory = definitions[family] || extendedDefinitions[family] || extendedDefinitions.fallback;
    return factory(title);
  };

  roots.forEach((root) => {
    const key = root.dataset.calculatorKey || "";
    const title = root.dataset.calculatorTitle || "this helper";
    const definition = getDefinition(key, title);
    renderFields(root, definition);
    const resultNode = root.querySelector("[data-calculator-result]");
    const update = () => {
      if (!resultNode) return;
      resultNode.innerHTML = makeHtml(definition.compute(getValues(root)));
    };
    root.addEventListener("input", update);
    root.addEventListener("change", update);
    update();
  });
})();
